/**
 * BG Reynolds – Batching Calculator v3
 * /assets/batching-calculator.js
 *
 * Changes in v3:
 * - Advanced mode toggle (wastage buffer, editable dilution %, equipment estimates)
 * - Serving size (portion override) only shown when pre-batched is selected
 * - Conditional dilution rate field — hidden for "made to order"
 * - Wastage line item appears in shopping list when buffer > 0
 * - Share link encodes advanced state (v3 format, backwards-compatible fallback)
 */

(function () {
  'use strict';

  let config = {};

  const state = {
    step: 1,
    drinkName: '',
    guests: 1,
    drinksPer: 1,
    servingSize: 'full',    // 'full' | numeric oz string
    portionMode: 'full',   // 'full' | 'fixed'
    dilution: 0.2,          // fraction, e.g. 0.2 = 20%
    preBatched: true,       // true = pre-batched, false = made to order
    advancedMode: false,
    wastage: 0,             // percentage, e.g. 10 = 10%
    outputUnit: 'oz',       // 'oz' | 'ml'
    ingredients: [],
    pickerOpen: false,
    pickerCat: 'syrups',
    pickerSelected: null,
    pickerCustom: '',
  };

  let root = null;

  /* ============================================================
     UTILS
     ============================================================ */

  function uid() { return Math.random().toString(36).slice(2, 9); }

  function oz(amount, unit) {
    const u = (unit || 'oz').toLowerCase();
    if (u === 'oz')   return parseFloat(amount) || 0;
    if (u === 'ml')   return (parseFloat(amount) || 0) / 29.5735;
    if (u === 'tsp')  return (parseFloat(amount) || 0) / 6;
    if (u === 'tbsp') return (parseFloat(amount) || 0) / 2;
    if (u === 'dash') return (parseFloat(amount) || 0) * 0.021;
    return parseFloat(amount) || 0;
  }

  function round2(n) { return Math.round(n * 100) / 100; }

  /** Convert an internal oz value to the current output unit for display */
  function displayVol(ozVal) {
    if (state.outputUnit === 'ml') {
      const ml = ozVal * 29.5735;
      // Use L for large values
      if (ml >= 1000) return round2(ml / 1000).toFixed(2) + ' L';
      return Math.round(ml) + ' ml';
    }
    return round2(ozVal) + ' oz';
  }

  /** Secondary stat label: gal in oz mode, L in ml mode */
  function displayVolSub(ozVal) {
    if (state.outputUnit === 'ml') {
      return round2(ozVal * 29.5735 / 1000).toFixed(2) + ' L';
    }
    return round2(ozVal / 128) + ' gal';
  }

  function toggleOutputUnit() {
    state.outputUnit = state.outputUnit === 'oz' ? 'ml' : 'oz';
    renderSidebars();
    renderIngTotals();
    if (state.step === 3) renderShoppingList();
    // Update toggle button label everywhere it appears
    root.querySelectorAll('[data-bc="unitToggle"]').forEach(btn => {
      btn.textContent = state.outputUnit === 'oz' ? 'Switch to ml' : 'Switch to oz';
    });
  }

  /** Re-render just the ingredient total badges without a full re-render */
  function renderIngTotals() {
    const calc = calcBatch();
    calc.scaledIngredients.forEach(ing => {
      const badge = root.querySelector(`[data-total-for="${ing.id}"]`);
      if (badge) badge.textContent = displayVol(ing.totalOzWithWastage || ing.totalOz);
    });
  }

  function stripBrand(name) { return name.replace(/^BG Reynolds\s*/i, '').trim(); }

  // Terms that disqualify an ingredient from matching a BG Reynolds syrup product.
  // BG Reynolds makes syrups only — not juices, nectars, pastes, concentrates, etc.
  const NOT_BGR_TERMS = /\b(juice|juices|nectar|paste|puree|purée|concentrate|seeds|seed|liqueur|liquor|bitters?|beer|wine|rum|gin|vodka|whiskey|whisky|tequila|brandy|cognac|soda|water|cream|milk|coconut water)\b/i;

  function matchSyrup(name) {
    // If the ingredient name contains a term that BG Reynolds doesn't make, skip matching
    if (NOT_BGR_TERMS.test(name)) return undefined;

    // Strip "BG Reynolds" prefix and filler words before matching.
    // Product titles like "Passion Fruit Tropical Cocktail Syrup" need to match
    // ingredient names like "BG Reynolds Passion Fruit Syrup" or just "Passion Fruit".
    const STOP = /\b(syrup|cocktail|tropical|original|mix|rich|lush|bitters|bitter|soda|water)\b/gi;
    const clean = n => stripBrand(n).toLowerCase().replace(STOP, '').replace(/[^a-z0-9]/g, '').trim();

    const normFull    = name.toLowerCase().replace(/[^a-z0-9]/g, '');
    const normClean   = clean(name);

    return (config.syrups || []).find(s => {
      const sNormFull  = s.title.toLowerCase().replace(/[^a-z0-9]/g, '');
      const sNormClean = clean(s.title);

      return sNormFull.includes(normFull)
          || sNormFull.includes(normClean)
          || normFull.includes(sNormFull)
          || (normClean.length > 2 && sNormClean.length > 2 && (
               sNormClean.includes(normClean)
            || normClean.includes(sNormClean)
          ));
    });
  }

  /**
   * Given a syrup product object and the total oz needed,
   * returns the best variant to add to cart:
   * - Parses bottle size from variant title (e.g. "375ml", "750ml", "1L")
   * - Converts to oz, then finds the smallest available bottle where
   *   ceil(neededOz / bottleSizeOz) is minimised (fewest bottles, right size)
   * - Falls back to the first available variant if nothing can be parsed
   */
  // Module-scope size parser (mirrors the one inside selectBestVariant) so
  // cart quantities can be computed at wiring time
  function parseVariantSizeOz(title) {
    if (!title) return null;
    const mlMatch  = title.match(/([\d.]+)\s*ml/i);
    const lMatch   = title.match(/([\d.]+)\s*l/i);
    const ozMatch  = title.match(/([\d.]+)\s*(?:fl\s*)?oz/i);
    if (mlMatch)  return parseFloat(mlMatch[1])  / 29.5735;
    if (lMatch)   return parseFloat(lMatch[1])   * 33.814;
    if (ozMatch)  return parseFloat(ozMatch[1]);
    return null;
  }

  // How many bottles of this variant cover the needed ounces
  function bottlesNeeded(variant, neededOz) {
    const sizeOz = parseVariantSizeOz(variant.title) || parseFloat(config.bottle_oz) || 25.4;
    return Math.max(1, Math.ceil(neededOz / sizeOz));
  }

  function selectBestVariant(syrup, neededOz) {
    const variants = (syrup.variants || []).filter(v => v.available);
    if (!variants.length) {
      // All sold out — return first variant anyway so button can show state
      return syrup.variants && syrup.variants[0] ? syrup.variants[0] : null;
    }

    // Parse ml or oz from variant title, convert to fl oz
    function parseSizeOz(title) {
      if (!title) return null;
      const mlMatch  = title.match(/([\d.]+)\s*ml/i);
      const lMatch   = title.match(/([\d.]+)\s*l/i);
      const ozMatch  = title.match(/([\d.]+)\s*(?:fl\s*)?oz/i);
      if (mlMatch)  return parseFloat(mlMatch[1])  / 29.5735;
      if (lMatch)   return parseFloat(lMatch[1])   * 33.814;
      if (ozMatch)  return parseFloat(ozMatch[1]);
      return null;
    }

    // Score each variant: prefer fewest bottles needed, then smallest bottle
    let best = null;
    let bestBottles = Infinity;
    let bestSizeOz  = Infinity;

    variants.forEach(v => {
      const sizeOz = parseSizeOz(v.title);
      if (!sizeOz) return;
      const bottles = Math.ceil(neededOz / sizeOz);
      // Prefer option that requires fewest bottles; tie-break on smaller bottle size
      if (bottles < bestBottles || (bottles === bestBottles && sizeOz < bestSizeOz)) {
        best = v;
        bestBottles = bottles;
        bestSizeOz  = sizeOz;
      }
    });

    // No size could be parsed — fall back to first available
    if (!best) best = variants[0];

    return best;
  }

  function autoCategory(name) {
    const n = name.toLowerCase();
    if ((config.syrups || []).some(s =>
      s.title.toLowerCase().includes(n) || n.includes(stripBrand(s.title).toLowerCase())
    )) return 'syrups';
    if (listFor('spirits').some(s => s.toLowerCase().includes(n) || n.includes(s.toLowerCase()))) return 'spirits';
    if (listFor('juices').some(s => s.toLowerCase().includes(n) || n.includes(s.toLowerCase()))) return 'juices';
    if (n.includes('bitter') || n.includes('angostura') || n.includes('peychaud')) return 'bitters';
    if (n.includes('syrup') || n.includes('orgeat') || n.includes('falernum')) return 'syrups';
    if (n.includes('juice') || n.includes('lime') || n.includes('lemon') || n.includes('orange') || n.includes('pineapple')) return 'juices';
    if (n.includes('rum') || n.includes('gin') || n.includes('vodka') ||
        n.includes('whisky') || n.includes('whiskey') || n.includes('tequila') ||
        n.includes('bourbon') || n.includes('cognac') || n.includes('brandy') || n.includes('mezcal')) return 'spirits';
    return 'other';
  }

  function listFor(cat) {
    const raw = (config.lists || {})[cat] || '';
    if (Array.isArray(raw)) return raw;
    return raw.split('\n').map(s => s.trim()).filter(Boolean);
  }

  /** Parse fl oz from a Shopify variant title like "375ml", "750ml", "1L", "16 oz" */
  function parseVariantOz(title) {
    if (!title) return null;
    const mlMatch = title.match(/([\d.]+)\s*ml/i);
    const lMatch  = title.match(/([\d.]+)\s*l/i);
    const ozMatch = title.match(/([\d.]+)\s*(?:fl\s*)?oz/i);
    if (mlMatch) return parseFloat(mlMatch[1]) / 29.5735;
    if (lMatch)  return parseFloat(lMatch[1])  * 33.814;
    if (ozMatch) return parseFloat(ozMatch[1]);
    return null;
  }

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/"/g, '&quot;')
      .replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  /* ============================================================
     CALCULATION
     ============================================================ */

  function calcBatch() {
    const multiplier = state.guests * state.drinksPer;
    let recipeOz = 0;
    state.ingredients.forEach(ing => { recipeOz += oz(ing.amount, ing.unit); });

    let servingOz;
    if (state.servingSize === 'full' || !state.preBatched) {
      servingOz = recipeOz;
    } else {
      const fixed = parseFloat(state.servingSize);
      servingOz = isNaN(fixed) ? recipeOz : fixed;
    }

    const scaleFactor = recipeOz > 0 ? servingOz / recipeOz : 1;
    const dilutionFraction = state.preBatched ? (parseFloat(state.dilution) || 0) : 0;
    const wastageFraction = (parseFloat(state.wastage) || 0) / 100;

    const ingredientOzBase  = round2(recipeOz * scaleFactor * multiplier);
    const waterOz           = round2(ingredientOzBase * dilutionFraction);
    const subtotalOz        = round2(ingredientOzBase + waterOz);
    const wastageOz         = round2(subtotalOz * wastageFraction);
    const totalOz           = round2(subtotalOz + wastageOz);
    const totalGal          = round2(totalOz / 128);
    const servingCount      = multiplier;

    const bottleOz  = parseFloat(config.bottle_oz) || 25.4;
    const buckets   = Math.ceil(totalOz / (5 * 128));
    const bottles750 = Math.ceil(totalOz / bottleOz);

    const scaledIngredients = state.ingredients.map(ing => {
      const ingOz = oz(ing.amount, ing.unit);
      const base  = round2(ingOz * scaleFactor * multiplier);
      const withWastage = round2(base * (1 + wastageFraction));
      return { ...ing, totalOz: base, totalOzWithWastage: withWastage };
    });

    return {
      servingCount, totalOz, totalGal,
      waterOz, wastageOz, subtotalOz,
      buckets, bottles750, bottleOz,
      scaledIngredients,
    };
  }

  /* ============================================================
     DOM HELPERS
     ============================================================ */

  function q(sel, ctx)   { return (ctx || root).querySelector(sel); }
  function qq(sel, ctx)  { return Array.from((ctx || root).querySelectorAll(sel)); }
  function bcq(key, ctx) { return (ctx || root).querySelector(`[data-bc="${key}"]`); }
  function bcqq(key)     { return Array.from(root.querySelectorAll(`[data-bc="${key}"]`)); }

  function h(tag, attrs, ...children) {
    const el = document.createElement(tag);
    Object.entries(attrs || {}).forEach(([k, v]) => {
      if (k === 'class') el.className = v;
      else if (k.startsWith('data-')) el.setAttribute(k, v);
      else el[k] = v;
    });
    children.forEach(child => {
      if (typeof child === 'string') el.insertAdjacentHTML('beforeend', child);
      else if (child) el.appendChild(child);
    });
    return el;
  }

  /* ============================================================
     STEP NAVIGATION
     ============================================================ */

  function goStep(n) {
    state.step = n;
    qq('[data-bc-step]').forEach(panel => {
      panel.classList.toggle('bc__stepPanel--hidden',
        parseInt(panel.getAttribute('data-bc-step'), 10) !== n);
    });
    qq('[data-bc-step-btn]').forEach(btn => {
      const s = parseInt(btn.getAttribute('data-bc-step-btn'), 10);
      btn.classList.remove('bc__step--active', 'bc__step--done');
      if (s === n) btn.classList.add('bc__step--active');
      else if (s < n) btn.classList.add('bc__step--done');
    });
    if (n === 3) renderShoppingList();
    renderSidebars();
    root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* ============================================================
     STARTER RECIPE CARDS
     ============================================================ */

  function renderStarters() {
    const container = bcq('starters');
    if (!container) return;
    let recipes = [];
    try { recipes = JSON.parse(config.recipes_json || '[]'); } catch (e) { /* ignore */ }

    container.innerHTML = '';

    if (!recipes.length) {
      // No example recipes — still show the import button
    } else {
      recipes.forEach((recipe, i) => {
        const card = h('button', { type: 'button', class: 'bc__starterCard', 'data-recipe-idx': String(i) });
        card.innerHTML = `
          <span class="bc__starterEmoji">${recipe.emoji || '🍹'}</span>
          <span class="bc__starterName">${escHtml(recipe.name || '')}</span>
          <span class="bc__starterHint">${escHtml(recipe.hint || '')}</span>
        `;
        card.addEventListener('click', () => loadRecipe(recipe, card));
        container.appendChild(card);
      });
    }

    // Always append the import button as the last item in the grid
    const importBtn = h('button', {
      type: 'button',
      class: 'bc__starterImportBtn',
      'data-bc-action': 'open-import',
      'aria-label': 'Import recipe from text',
    });
    importBtn.innerHTML = `
      <span class="bc__starterImportIcon">✦</span>
      <span>Import recipe</span>
    `;
    container.appendChild(importBtn);
  }

  function loadRecipe(recipe, cardEl) {
    qq('.bc__starterCard').forEach(c => c.classList.remove('bc__starterCard--selected'));
    cardEl.classList.add('bc__starterCard--selected');
    state.drinkName = recipe.name || '';
    const nameInput = bcq('drinkName');
    if (nameInput) nameInput.value = state.drinkName;
    state.ingredients = (recipe.ingredients || []).map(ing => ({
      id: uid(),
      name: ing.name || '',
      amount: ing.amount || 1,
      unit: ing.unit || 'oz',
      category: ing.category || autoCategory(ing.name || ''),
    }));
    renderIngredients();
    renderSidebars();
  }

  /* ============================================================
     INGREDIENT CARDS
     ============================================================ */

  const CAT_LABELS = { syrups: 'Syrup', spirits: 'Spirit', juices: 'Juice', bitters: 'Bitters', other: 'Other' };

  function renderIngredients() {
    const list = bcq('ingList');
    if (!list) return;
    list.innerHTML = '';
    if (!state.ingredients.length) {
      list.appendChild(h('p', { class: 'bc__hint', style: 'text-align:center;padding:1rem 0;' },
        'Add your first ingredient below to get started.'));
      return;
    }
    state.ingredients.forEach(ing => list.appendChild(buildIngCard(ing)));
  }

  function buildIngCard(ing) {
    const card = h('div', { class: 'bc__ingCard', 'data-ing-id': ing.id });
    const catClass = `bc__catPill--${ing.category || 'other'}`;
    const isSyrup = ing.category === 'syrups';
    card.innerHTML = `
      <div class="bc__ingTop">
        <span class="bc__catPill ${catClass}">${isSyrup ? '🌿 ' : ''}${CAT_LABELS[ing.category] || 'Other'}</span>
        <button type="button" class="bc__ingRemove" aria-label="Remove ingredient">×</button>
      </div>
      <div class="bc__ingFields">
        <input type="text"   class="bc__input bc__ingName"   value="${escHtml(ing.name)}"   placeholder="Ingredient name" aria-label="Ingredient name" />
        <input type="number" class="bc__input bc__ingAmount" value="${ing.amount}" min="0" step="0.25" aria-label="Amount" />
        <select class="bc__select bc__ingUnit bc__ingUnitSelect" aria-label="Unit">
          ${['oz','ml','tsp','tbsp','dash'].map(u => `<option value="${u}"${ing.unit===u?' selected':''}>${u}</option>`).join('')}
        </select>
        <div class="bc__totalBadge" data-total-for="${ing.id}">—</div>
      </div>
    `;
    card.querySelector('.bc__ingRemove').addEventListener('click', () => {
      state.ingredients = state.ingredients.filter(i => i.id !== ing.id);
      renderIngredients(); renderSidebars();
    });
    card.querySelector('.bc__ingName').addEventListener('input', e => {
      const i = state.ingredients.find(x => x.id === ing.id);
      if (!i) return;
      i.name = e.target.value;
      const newCat = autoCategory(e.target.value);
      if (newCat !== i.category) {
        i.category = newCat;
        const existing = root.querySelector(`[data-ing-id="${ing.id}"]`);
        if (existing) existing.replaceWith(buildIngCard(i));
      }
      renderSidebars();
    });
    card.querySelector('.bc__ingAmount').addEventListener('input', e => {
      const i = state.ingredients.find(x => x.id === ing.id);
      if (i) { i.amount = parseFloat(e.target.value) || 0; renderSidebars(); }
    });
    card.querySelector('.bc__ingUnitSelect').addEventListener('change', e => {
      const i = state.ingredients.find(x => x.id === ing.id);
      if (i) { i.unit = e.target.value; renderSidebars(); }
    });
    return card;
  }

  function updateIngTotals(scaledIngredients) {
    scaledIngredients.forEach(ing => {
      const badge = root.querySelector(`[data-total-for="${ing.id}"]`);
      if (badge) badge.textContent = displayVol(ing.totalOzWithWastage || ing.totalOz);
    });
  }

  /* ============================================================
     SERVING METHOD — show/hide portion size
     ============================================================ */

  function applyServingMethod() {
    const portionWrap  = bcq('portionWrap');
    const dilRateGroup = bcq('dilutionRateGroup');

    if (portionWrap) {
      portionWrap.classList.toggle('bc__portionWrap--hidden', !state.preBatched);
    }
    // When made-to-order, reset serving size to full
    if (!state.preBatched) {
      state.servingSize = 'full';
      state.portionMode = 'full';
      selectPortionMode('full');
    } else {
      // Re-apply current portion mode so UI is consistent
      selectPortionMode(state.portionMode || 'full');
    }
    if (dilRateGroup) {
      dilRateGroup.style.opacity = state.preBatched ? '1' : '0.4';
      dilRateGroup.style.pointerEvents = state.preBatched ? '' : 'none';
    }
  }

  function initDilutionOpts() {
    bcqq('dilutionOpt').forEach(opt => {
      opt.addEventListener('click', () => {
        bcqq('dilutionOpt').forEach(o => o.classList.remove('bc__dilutionOpt--active'));
        opt.classList.add('bc__dilutionOpt--active');
        const val = parseFloat(opt.getAttribute('data-value')) || 0;
        state.preBatched = val > 0;
        state.dilution = state.preBatched ? val : 0;
        applyServingMethod();
        renderSidebars();
      });
    });
    // Init from active state
    const active = q('.bc__dilutionOpt--active');
    if (active) {
      const val = parseFloat(active.getAttribute('data-value')) || 0;
      state.preBatched = val > 0;
      state.dilution = val;
    }
    applyServingMethod();
  }

  function initPortionOpts() {
    bcqq('portionOpt').forEach(opt => {
      opt.addEventListener('click', () => {
        const mode = opt.getAttribute('data-portion');
        selectPortionMode(mode);
      });
    });

    // Fixed pour value/unit inputs
    const pourVal  = bcq('pourSizeValue');
    const pourUnit = bcq('pourSizeUnit');
    if (pourVal) pourVal.addEventListener('input', e => {
      updateServingSizeFromPour();
      renderSidebars();
    });
    if (pourUnit) pourUnit.addEventListener('change', () => {
      updateServingSizeFromPour();
      renderSidebars();
    });
  }

  function selectPortionMode(mode) {
    state.portionMode = mode; // 'full' | 'fixed'
    bcqq('portionOpt').forEach(o => {
      const isActive = o.getAttribute('data-portion') === mode;
      o.classList.toggle('bc__portionOpt--active', isActive);
    });
    // Show/hide the inline input
    const inputWrap = bcq('fixedPourInput');
    if (inputWrap) inputWrap.classList.toggle('bc__fixedPourInput--hidden', mode !== 'fixed');

    if (mode === 'full') {
      state.servingSize = 'full';
    } else {
      updateServingSizeFromPour();
    }
    renderSidebars();
  }

  function updateServingSizeFromPour() {
    const valEl  = bcq('pourSizeValue');
    const unitEl = bcq('pourSizeUnit');
    const val    = parseFloat(valEl?.value) || 6;
    const unit   = unitEl?.value || 'oz';
    // Convert to oz for internal state (calcBatch works in oz)
    state.servingSize = unit === 'ml' ? String(round2(val / 29.5735)) : String(val);
  }

  /* ============================================================
     ADVANCED MODE
     ============================================================ */

  function toggleAdvancedMode() {
    state.advancedMode = !state.advancedMode;
    const panel = bcq('advancedPanel');
    const icon  = bcq('advancedToggleIcon');
    if (panel) panel.classList.toggle('bc__advancedPanel--hidden', !state.advancedMode);
    if (icon)  icon.classList.toggle('bc__advancedToggleIcon--open', state.advancedMode);

    // Show/hide equipment in shopping list (step 3)
    const shopEquip = bcq('shopEquipBlock');
    if (shopEquip) shopEquip.classList.toggle('bc__shopEquip--hidden', !state.advancedMode);

    renderSidebars();
  }

  function initAdvancedControls() {
    // Wastage input
    const wastageInput = bcq('wastage');
    if (wastageInput) {
      wastageInput.addEventListener('input', e => {
        state.wastage = Math.max(0, Math.min(50, parseFloat(e.target.value) || 0));
        renderSidebars();
        if (state.step === 3) renderShoppingList();
      });
    }

    // Editable dilution rate
    const dilRateInput = bcq('dilutionRate');
    if (dilRateInput) {
      dilRateInput.addEventListener('input', e => {
        const pct = Math.max(0, Math.min(50, parseFloat(e.target.value) || 0));
        state.dilution = pct / 100;
        renderSidebars();
      });
    }
  }

  /* ============================================================
     SIDEBAR
     ============================================================ */

  function renderSidebars() {
    const calc = calcBatch();
    updateIngTotals(calc.scaledIngredients);

    // Update equipment in advanced panel (step 2)
    const bucketsEl    = bcq('buckets');
    const bottles750El = bcq('bottles750');
    if (bucketsEl)    bucketsEl.textContent    = calc.buckets;
    if (bottles750El) bottles750El.textContent = calc.bottles750;

    qq('.bc__sidebar .bc__panel').forEach(panel => {
      panel.innerHTML = buildSidebarHTML(calc);
      const allEntries = [];
      panel.querySelectorAll('.bc__syrupShopBtn').forEach(btn => {
        const syrupHandle = btn.getAttribute('data-handle');
        const available   = btn.getAttribute('data-available') === 'true';
        const neededOz    = parseFloat(btn.getAttribute('data-needed-oz')) || 0;
        const syrup       = syrupHandle
          ? (config.syrups || []).find(s => s.handle === syrupHandle) : null;
        if (!available) {
          btn.textContent   = 'Out of stock';
          btn.disabled      = true;
          btn.style.opacity = '0.5';
        } else if (syrup) {
          const bestVar = selectBestVariant(syrup, neededOz);
          if (bestVar) {
            const qty = bottlesNeeded(bestVar, neededOz);
            if (qty > 1) btn.textContent = 'Add ' + qty + ' bottles';
            allEntries.push({ id: bestVar.id, quantity: qty });
            btn.addEventListener('click', () => addToCart(bestVar, qty, btn));
          }
        } else {
          btn.addEventListener('click', () => {
            const url = btn.getAttribute('data-url');
            if (url) window.location.href = url;
          });
        }
      });
      const allBtn = panel.querySelector('.bc__syrupShopAllBtn');
      if (allBtn) {
        if (allEntries.length > 1) {
          allBtn.addEventListener('click', () => addAllToCart(allEntries, allBtn));
        } else {
          allBtn.style.display = 'none';
        }
      }
    });
  }

  function buildSidebarHTML(calc) {
    const bottleOz    = calc.bottleOz;
    const bottleLabel = config.bottle_label || '750ml';

    const syrupsInBatch = calc.scaledIngredients.filter(i =>
      i.category === 'syrups' && matchSyrup(i.name)
    );

    let syrupPromoHTML = '';
    if (syrupsInBatch.length) {
      const items = syrupsInBatch.map(i => {
        const match   = matchSyrup(i.name);
        const totalOz = i.totalOzWithWastage || i.totalOz;
        const bottles = Math.ceil(totalOz / bottleOz);
        const avail     = match && match.available ? 'true' : 'false';
        const shopLabel = match && !match.available ? 'Out of stock' : 'Add to cart';
        return `
          <div class="bc__syrupItem">
            <div>
              <div class="bc__syrupName">${escHtml(stripBrand(match ? match.title : i.name))}</div>
              <div class="bc__syrupQty">${displayVol(totalOz)} · ${bottles} ${bottles===1?'bottle':'bottles'} needed</div>
            </div>
            <button class="bc__syrupShopBtn"
              data-url="${escHtml(match ? match.url : '#')}"
              data-handle="${escHtml(match ? match.handle : '')}"
              data-available="${avail}"
              data-needed-oz="${round2(totalOz)}"
            >${shopLabel}</button>
          </div>
        `;
      }).join('');
      syrupPromoHTML = `
        <div class="bc__syrupPromo">
          <div class="bc__syrupPromoHead">BG Reynolds syrups in this batch</div>
          ${items}
          <button type="button" class="bc__btn bc__syrupShopAllBtn" style="width:100%;margin-top:10px">Add every bottle to cart</button>
        </div>
      `;
    }

    const wastageNote = state.wastage > 0
      ? `<div class="bc__statBlock"><div class="bc__statLabel">Overage buffer (${state.wastage}%)</div><div class="bc__statValue">${displayVol(calc.wastageOz)}</div></div>`
      : '';

    return `
      <div class="bc__panelTitle">Batch totals</div>
      <div class="bc__statBlock">
        <div class="bc__statLabel">Servings</div>
        <div class="bc__statValue">${calc.servingCount}</div>
      </div>
      <div class="bc__statBlock">
        <div class="bc__statLabel">Total volume</div>
        <div class="bc__statValue">${displayVol(calc.totalOz)}</div>
        <div class="bc__statSub">${displayVolSub(calc.totalOz)}</div>
      </div>
      ${state.preBatched ? `
      <div class="bc__statBlock">
        <div class="bc__statLabel">Water (dilution)</div>
        <div class="bc__statValue">${displayVol(calc.waterOz)}</div>
      </div>` : ''}
      ${wastageNote}
      ${syrupPromoHTML}
      <div class="bc__sidebarShare">
        <button type="button" class="bc__btn bc__btn--secondary" data-bc-action="toggle-unit" data-bc="unitToggle" style="flex:0 0 auto">
          ${state.outputUnit === 'oz' ? 'ml' : 'oz'}
        </button>
        <button type="button" class="bc__btn bc__btn--secondary" data-bc-action="copy-link" style="flex:1">
          <span aria-hidden="true">⎘</span> <span data-bc="linkStatus">Copy link</span>
        </button>
      </div>
    `;
  }

  /* ============================================================
     INGREDIENT PICKER MODAL
     ============================================================ */

  function openPicker() {
    state.pickerOpen = true;
    state.pickerSelected = null;
    state.pickerCustom = '';
    const modal = bcq('pickerModal');
    if (!modal) return;
    modal.hidden = false;
    switchPickerCat('syrups');
    const search = bcq('pickerSearch');
    if (search) search.value = '';
  }

  function closePicker() {
    state.pickerOpen = false;
    const modal = bcq('pickerModal');
    if (modal) modal.hidden = true;
  }

  function switchPickerCat(cat) {
    state.pickerCat = cat;
    state.pickerSelected = null;
    qq('[data-bc="catTabs"] .bc__catTab').forEach(tab =>
      tab.classList.toggle('bc__catTab--active', tab.getAttribute('data-cat') === cat)
    );
    renderPickerList();
  }

  function renderPickerList() {
    const list = bcq('pickerList');
    if (!list) return;
    list.innerHTML = '';
    const cat = state.pickerCat;
    let items = [];
    if (cat === 'syrups') {
      (config.syrups || []).forEach(s => items.push({ name: s.title, bgr: true }));
      listFor('other').filter(n => n.toLowerCase().includes('syrup'))
        .forEach(n => items.push({ name: n, bgr: false }));
    } else {
      listFor(cat).forEach(n => items.push({ name: n, bgr: false }));
    }
    if (!items.length) {
      list.innerHTML = `<p style="padding:0.75rem;font-size:0.85rem;color:#7A9970;">No suggestions — use the text field below.</p>`;
      return;
    }
    items.forEach(item => {
      const btn = h('button', { type: 'button', class: `bc__pickerItem${item.bgr ? ' bc__pickerItem--bgr' : ''}` });
      btn.innerHTML = `<span>${escHtml(item.name)}</span>${item.bgr ? '<span class="bc__bgrBadge">BG Reynolds</span>' : ''}`;
      btn.addEventListener('click', () => {
        state.pickerSelected = item.name;
        state.pickerCustom = '';
        const search = bcq('pickerSearch');
        if (search) search.value = '';
        qq('.bc__pickerItem').forEach(i => i.classList.remove('bc__pickerItem--selected'));
        btn.classList.add('bc__pickerItem--selected');
      });
      list.appendChild(btn);
    });
  }

  function confirmPicker() {
    const search = bcq('pickerSearch');
    const name = state.pickerSelected || (search ? search.value.trim() : '') || state.pickerCustom;
    if (!name) return;
    const cat = autoCategory(name) !== 'other' ? autoCategory(name) : state.pickerCat;
    state.ingredients.push({ id: uid(), name, amount: 1, unit: 'oz', category: cat });
    closePicker();
    renderIngredients();
    renderSidebars();
  }

  /* ============================================================
     SHOPPING LIST (Step 3)
     ============================================================ */

  const CAT_ORDER   = ['spirits', 'syrups', 'juices', 'bitters', 'other'];
  const CAT_DISPLAY = { spirits: 'Spirits', syrups: 'BG Reynolds syrups', juices: 'Juices', bitters: 'Bitters', other: 'Other' };

  /* ============================================================
     JUICE BREAKDOWN — container sizes + fresh yield
     ============================================================ */

  // Built-in container sizes in oz (name fragment → oz per container)
  // Matched case-insensitively against ingredient names
  const JUICE_CONTAINERS_DEFAULT = [
    { match: 'pineapple',   oz: 46,   label: '46 oz can (Dole)' },
    { match: 'lime',        oz: 32,   label: '32 oz bottle' },
    { match: 'lemon',       oz: 32,   label: '32 oz bottle' },
    { match: 'orange',      oz: 52,   label: '52 oz carton' },
    { match: 'grapefruit',  oz: 32,   label: '32 oz bottle' },
    { match: 'cranberry',   oz: 32,   label: '32 oz bottle' },
    { match: 'passion fruit', oz: 33.8, label: '1L bottle' },
    { match: 'guava',       oz: 33.8, label: '1L bottle' },
    { match: 'mango',       oz: 33.8, label: '1L bottle' },
    { match: 'coconut',     oz: 13.5, label: '13.5 oz can' },
    { match: 'pomegranate', oz: 16,   label: '16 oz bottle' },
    { match: 'watermelon',  oz: 16,   label: '16 oz bottle' },
  ];

  // Fresh fruit yields — avg oz of juice per piece
  // Only populated for fruits where squeezing fresh is realistic
  const FRESH_YIELD = [
    { match: 'lime',        ozPerPiece: 1.0,  piece: 'lime' },
    { match: 'lemon',       ozPerPiece: 1.5,  piece: 'lemon' },
    { match: 'orange',      ozPerPiece: 3.5,  piece: 'orange' },
    { match: 'grapefruit',  ozPerPiece: 6.0,  piece: 'grapefruit' },
    { match: 'blood orange', ozPerPiece: 2.5, piece: 'blood orange' },
  ];

  // Parse the admin-editable "juice_containers" setting
  // Format per line: "Pineapple Juice: 46" or "Pineapple Juice: 46, 46 oz can (Dole)"
  function parseJuiceContainerSettings() {
    const raw = config.juice_containers || '';
    const overrides = [];
    raw.split('\n').forEach(line => {
      line = line.trim();
      if (!line) return;
      const colonIdx = line.lastIndexOf(':');
      if (colonIdx === -1) return;
      const name  = line.slice(0, colonIdx).trim().toLowerCase();
      const rest  = line.slice(colonIdx + 1).trim();
      const parts = rest.split(',');
      const oz    = parseFloat(parts[0]);
      const label = parts[1] ? parts[1].trim() : `${oz} oz`;
      if (name && oz > 0) overrides.push({ match: name, oz, label });
    });
    return overrides;
  }

  /**
   * Given an ingredient name and total oz needed, returns juice breakdown info:
   * {
   *   containers: [{ oz, label, count }],  // container size options
   *   fresh: { piece, count } | null        // fresh fruit equivalent (if applicable)
   * }
   * Returns null if ingredient doesn't look like a juice.
   */
  function getJuiceInfo(name, totalOz) {
    const n = name.toLowerCase();

    // Must contain a juice-like keyword to show breakdown
    if (!n.includes('juice') && !n.includes('nectar') && !n.includes('puree') && !n.includes('purée')) return null;

    // Merge defaults with admin overrides (overrides win on matching names)
    const adminOverrides = parseJuiceContainerSettings();
    const containers = [...JUICE_CONTAINERS_DEFAULT];
    adminOverrides.forEach(ov => {
      const idx = containers.findIndex(c => c.match === ov.match);
      if (idx >= 0) containers[idx] = ov; // replace
      else containers.push(ov);           // add new
    });

    // Find matching container size
    const containerMatch = containers.find(c => n.includes(c.match));

    // Find fresh yield match
    const freshMatch = FRESH_YIELD.find(f => n.includes(f.match));

    // Nothing matched — return generic 32 oz
    const containerOz    = containerMatch ? containerMatch.oz    : 32;
    const containerLabel = containerMatch ? containerMatch.label : '32 oz bottle';
    const containerCount = Math.ceil(totalOz / containerOz);

    const info = {
      containerOz,
      containerLabel,
      containerCount,
      fresh: freshMatch
        ? { piece: freshMatch.piece, count: Math.ceil(totalOz / freshMatch.ozPerPiece) }
        : null,
    };

    return info;
  }

  function renderShoppingList() {
    const calc = calcBatch();
    const bottleOz    = calc.bottleOz;
    const bottleLabel = config.bottle_label || '750ml';

    const titleEl = bcq('shopTitle');
    if (titleEl) titleEl.textContent = state.drinkName ? `Shopping list — ${state.drinkName}` : 'Shopping list';

    const metaEl = bcq('shopMeta');
    if (metaEl) {
      const dilLabel = state.preBatched ? 'Pre-batched' : 'Made to order';
      const sizeOzNum = parseFloat(state.servingSize);
      const sizeLabel = state.preBatched && state.portionMode === 'fixed' && state.servingSize !== 'full'
        ? ` · ${state.outputUnit === 'ml' ? Math.round(sizeOzNum * 29.5735) + ' ml' : round2(sizeOzNum) + ' oz'} pours` : '';
      const wastageLabel = state.wastage > 0 ? ` · +${state.wastage}% overage` : '';
      metaEl.textContent = `${calc.servingCount} servings · ${dilLabel}${sizeLabel}${wastageLabel}`;
    }

    // Group ingredients
    const groups = {};
    CAT_ORDER.forEach(c => { groups[c] = []; });
    calc.scaledIngredients.forEach(ing => {
      const cat = ing.category || 'other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(ing);
    });

    // Water (dilution) — only for pre-batched
    if (state.preBatched && calc.waterOz > 0) {
      groups.other.push({ id: '__water__', name: 'Water (dilution)', totalOz: calc.waterOz, totalOzWithWastage: round2(calc.waterOz * (1 + (state.wastage||0)/100)), isWater: true });
    }

    const body = bcq('shopBody');
    if (!body) return;
    body.innerHTML = '';

    CAT_ORDER.forEach(cat => {
      const items = groups[cat];
      if (!items.length) return;
      body.appendChild(h('div', { class: 'bc__shopSectionHead' }, CAT_DISPLAY[cat] || cat));

      items.forEach(item => {
        const displayOz = item.totalOzWithWastage || item.totalOz;
        const bottles   = item.isWater ? null : Math.ceil(displayOz / bottleOz);
        const syrupMatch = !item.isWater && item.category === 'syrups' ? matchSyrup(item.name) : null;

        // Juice breakdown (container sizes + fresh yield)
        const juiceInfo = (!item.isWater && item.category === 'juices')
          ? getJuiceInfo(item.name, displayOz)
          : null;

        const row     = h('div', { class: 'bc__shopItem' + (juiceInfo ? ' bc__shopItem--juice' : '') });
        const leftDiv = h('div', { style: 'flex:1;min-width:0' });
        leftDiv.innerHTML = `<div class="bc__shopItemName">${escHtml(item.name)}</div>`;
        if (syrupMatch) leftDiv.innerHTML += `<div class="bc__shopItemStock">In stock · Shop BG Reynolds</div>`;
        if (state.wastage > 0 && !item.isWater) {
          leftDiv.innerHTML += `<div class="bc__shopItemWastage">Includes ${state.wastage}% overage</div>`;
        }

        // Inline juice breakdown
        if (juiceInfo) {
          const freshLine = juiceInfo.fresh
            ? `<div class="bc__juiceBreakdownRow bc__juiceBreakdownFresh">
                <span class="bc__juiceBreakdownDot"></span>
                ~${juiceInfo.fresh.count} fresh ${juiceInfo.fresh.piece}${juiceInfo.fresh.count !== 1 ? 's' : ''}
               </div>`
            : '';
          leftDiv.innerHTML += `
            <div class="bc__juiceBreakdown">
              <div class="bc__juiceBreakdownRow">
                <span class="bc__juiceBreakdownDot"></span>
                ${juiceInfo.containerCount} × ${juiceInfo.containerLabel}
              </div>
              ${freshLine}
            </div>`;
        }

        const rightDiv = h('div', { class: 'bc__shopItemRight' });
        const qtyDiv   = h('div', {});
        qtyDiv.innerHTML = `
          <div class="bc__shopItemQty">${displayVol(displayOz)}</div>
          <div class="bc__shopItemBottles">${
            item.isWater
              ? state.outputUnit === 'ml' ? Math.round(displayOz * 29.5735) + ' ml' : `~${round2(displayOz / 8)} cups`
              : juiceInfo
                ? '' // container info already shown inline on left side
                : `~${bottles} ${bottleLabel} bottle${bottles !== 1 ? 's' : ''}`
          }</div>
        `;
        rightDiv.appendChild(qtyDiv);

        if (syrupMatch) {
          const neededOz  = displayOz;
          const bestVar   = selectBestVariant(syrupMatch, neededOz);
          if (bestVar) {
            const btn = h('button', { type: 'button', class: 'bc__shopCartBtn' }, 'Add to cart');
            if (!bestVar.available) {
              btn.textContent = 'Out of stock';
              btn.disabled    = true;
              btn.style.opacity = '0.5';
            } else {
              // Calculate how many of the best variant are needed
              const varSizeOz  = parseVariantOz(bestVar.title) || parseFloat(config.bottle_oz) || 25.4;
              const varBottles = Math.ceil(neededOz / varSizeOz);
              btn.addEventListener('click', () => addToCart(bestVar, varBottles, btn));
            }
            rightDiv.appendChild(btn);
          }
        }

        row.appendChild(leftDiv);
        row.appendChild(rightDiv);
        body.appendChild(row);
      });
    });

    // Equipment (advanced mode only)
    const shopEquip = bcq('shopEquipBlock');
    if (shopEquip) shopEquip.classList.toggle('bc__shopEquip--hidden', !state.advancedMode);

    const bucketsShop    = bcq('bucketsShop');
    const bottles750Shop = bcq('bottles750Shop');
    if (bucketsShop)    bucketsShop.textContent    = calc.buckets;
    if (bottles750Shop) bottles750Shop.textContent = calc.bottles750;
  }

  /* ============================================================
     RECIPE IMPORT — AI-POWERED
     ============================================================ */

  // Parsed result stored here between parse and confirm steps
  let importParsed = [];

  function openImportModal() {
    const modal = root.querySelector('[data-bc="importModal"]');
    if (!modal) return;
    modal.hidden = false;
    showImportStep('A');
    // Clear previous state
    const txt = root.querySelector('[data-bc="importText"]');
    const nm  = root.querySelector('[data-bc="importName"]');
    const err = root.querySelector('[data-bc="importError"]');
    if (txt) txt.value = '';
    if (nm)  nm.value  = '';
    if (err) { err.hidden = true; err.textContent = ''; }
    importParsed = [];
    if (txt) setTimeout(() => txt.focus(), 60);
  }

  function closeImportModal() {
    const modal = root.querySelector('[data-bc="importModal"]');
    if (modal) modal.hidden = true;
  }

  function showImportStep(step) {
    const a = root.querySelector('[data-bc="importStepA"]');
    const b = root.querySelector('[data-bc="importStepB"]');
    const previewCard = root.querySelector('[data-bc="importPreviewCard"]');
    if (a) a.hidden = (step !== 'A');
    if (b) b.hidden = true; // modal step B is now replaced by inline card
    if (step === 'B') {
      // Close the modal, show the inline preview card instead
      closeImportModal();
      if (previewCard) previewCard.hidden = false;
      previewCard?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      if (previewCard) previewCard.hidden = true;
    }
  }

  function parseRecipeWithAI() {
    const txt = root.querySelector('[data-bc="importText"]');
    const raw = txt ? txt.value.trim() : '';
    if (!raw) { showImportError('Please paste a recipe first.'); return; }

    const errEl = root.querySelector('[data-bc="importError"]');
    if (errEl) { errEl.hidden = true; errEl.textContent = ''; }

    try {
      const parsed = parseRecipeText(raw);
      if (!parsed.length) throw new Error('No ingredients found in that text.');
      importParsed = parsed;
      renderImportPreview(parsed);
      showImportStep('B');
    } catch (err) {
      showImportError(err.message.includes('No ingredients')
        ? err.message
        : 'Could not parse the recipe. Try pasting one ingredient per line, e.g. "2 oz Dark Rum".');
    }
  }

  /* ---- Recipe text parser ---- */
  function parseRecipeText(raw) {
    // Normalise unicode fractions
    const normFrac = s => s
      .replace(/½/g, '1/2').replace(/¼/g, '1/4').replace(/¾/g, '3/4')
      .replace(/⅓/g, '1/3').replace(/⅔/g, '2/3').replace(/⅛/g, '1/8')
      .replace(/⅕/g, '1/5').replace(/·/g, '').replace(/﻿/g, '');

    function parseFrac(s) {
      s = s.trim();
      const mixed = s.match(/^(\d+)\s+(\d+)\/(\d+)$/);
      if (mixed) return parseInt(mixed[1]) + parseInt(mixed[2]) / parseInt(mixed[3]);
      const frac = s.match(/^(\d+)\/(\d+)$/);
      if (frac) return parseInt(frac[1]) / parseInt(frac[2]);
      return parseFloat(s) || 0;
    }

    const UNITS = {
      'oz': 'oz', 'ounce': 'oz', 'ounces': 'oz', 'fl oz': 'oz',
      'fluid ounce': 'oz', 'fluid ounces': 'oz',
      'ml': 'ml', 'milliliter': 'ml', 'milliliters': 'ml',
      'millilitre': 'ml', 'millilitres': 'ml',
      'tsp': 'tsp', 'teaspoon': 'tsp', 'teaspoons': 'tsp', 't': 'tsp',
      'tbsp': 'tbsp', 'tablespoon': 'tbsp', 'tablespoons': 'tbsp',
      'tbs': 'tbsp', 'tb': 'tbsp',
      'dash': 'dash', 'dashes': 'dash', 'drop': 'dash', 'drops': 'dash',
      'barspoon': 'tsp', 'bar spoon': 'tsp',
      'rinse': 'dash', 'splash': 'dash',
      'cup': 'oz', 'cups': 'oz', 'cl': 'ml', 'l': 'ml',
    };

    function convertUnit(amount, rawUnit) {
      const ru = rawUnit.toLowerCase().trim().replace(/\.$/, '');
      if (ru === 'cup' || ru === 'cups') return [amount * 8, 'oz'];
      if (ru === 'cl')   return [amount * 10, 'ml'];
      if (ru === 'l')    return [amount * 1000, 'ml'];
      const mapped = UNITS[ru];
      return [amount, mapped || 'oz'];
    }

    const SKIP = /garnish|muddle|shake|stir|strain|serve|fill|^top |float|^ice|rim|wedge|twist|sprig|cherry|sugar|salt|combine|pour|build|add to|layer|instruction|direction|method|preparation|optional/i;

    // Matches: optional amount (number/fraction) + optional unit + ingredient name
    const LINE_RE = /^([\d\/\s]+)\s*(oz\.?|ounces?|fl\.?\s*oz\.?|ml|millilite?rs?|tbsp\.?|tbs\.?|tablespoons?|tsp\.?|teaspoons?|barspoons?|bar\s*spoons?|dashes?|drops?|rinse|splash|cl|cups?|[tT]\.?)?\s*(.+)/;

    const results = [];
    const normalized = normFrac(raw);
    const inputLines = normalized.split('\n');

    for (let line of inputLines) {
      line = line.trim();
      if (!line) continue;
      if (SKIP.test(line)) continue;
      if (/^[A-Z][A-Z\s:]+$/.test(line)) continue; // heading

      const m = line.match(LINE_RE);
      if (!m) continue;

      const rawAmount = m[1].trim();
      const rawUnit   = (m[2] || '').trim().replace(/\.$/, '');
      let   rawName   = m[3].trim().replace(/^[,\-–—]+\s*/, '').trim();

      // Strip trailing parenthetical notes like "(freshly squeezed)"
      rawName = rawName.replace(/\s*\(.*?\)\s*$/, '').trim();

      if (!rawName || rawName.length < 2) continue;

      const amount = parseFrac(rawAmount);
      if (amount <= 0) continue;

      const [finalAmount, finalUnit] = convertUnit(amount, rawUnit || 'oz');
      const category = autoCategory(rawName);

      results.push({
        name:     rawName.charAt(0).toUpperCase() + rawName.slice(1),
        amount:   round2(finalAmount),
        unit:     finalUnit,
        category,
      });
    }

    return results;
  }


  function showImportError(msg) {
    const errEl = root.querySelector('[data-bc="importError"]');
    if (errEl) { errEl.textContent = msg; errEl.hidden = false; }
  }

  function renderImportPreview(parsed) {
    const container = root.querySelector('[data-bc="importPreview"]');
    const warnEl    = root.querySelector('[data-bc="importWarning"]');
    if (!container) return;

    const warnings = [];

    let html = '';

    parsed.forEach((ing, i) => {
      // Flag anything that might need attention
      if (!ing.amount || ing.amount === 0)   warnings.push(`"${ing.name}" has no amount — check it.`);
      if (ing.unit === '' && ing.amount > 0)  warnings.push(`"${ing.name}" has no unit — assumed whole pieces.`);

      // Check if it's a BG Reynolds syrup
      const syrupMatch = matchSyrup(ing.name);
      const isSyrup = ing.category === 'syrups';
      const bgrBadge = syrupMatch
        ? `<span class="bc__bgrBadge">BG Reynolds</span>`
        : '';

      const catColors = {
        syrups:  'var(--bc-pill-syrup-bg);color:var(--bc-pill-syrup-fg)',
        spirits: 'var(--bc-pill-spirit-bg);color:var(--bc-pill-spirit-fg)',
        juices:  'var(--bc-pill-juice-bg);color:var(--bc-pill-juice-fg)',
        bitters: 'var(--bc-pill-bitters-bg);color:var(--bc-pill-bitters-fg)',
        other:   'var(--bc-pill-other-bg);color:var(--bc-pill-other-fg)',
      };
      const catStyle = catColors[ing.category] || catColors.other;

      html += `
        <div class="bc__previewItem">
          <span style="font-weight:500">${escHtml(ing.name)} ${bgrBadge}</span>
          <span style="font-family:var(--mono)">${ing.amount}</span>
          <span style="color:#7A9970">${ing.unit || '—'}</span>
          <span class="bc__catPill" style="background:${catStyle};font-size:0.68rem;padding:2px 8px">
            ${ing.category}
          </span>
        </div>`;
    });

    container.innerHTML = html;

    // Warnings
    if (warnEl) {
      if (warnings.length) {
        warnEl.innerHTML = '⚠ ' + warnings.join('<br>⚠ ');
        warnEl.hidden = false;
      } else {
        warnEl.hidden = true;
      }
    }
  }

  function confirmImport() {
    if (!importParsed.length) return;

    // Grab optional name from import field
    const nameInput = root.querySelector('[data-bc="importName"]');
    const importedName = nameInput ? nameInput.value.trim() : '';

    // Replace empty default slots or append
    const hasOnlyBlanks = state.ingredients.length > 0
      && state.ingredients.every(i => !i.name);

    if (hasOnlyBlanks) {
      state.ingredients = [];
    }

    importParsed.forEach(ing => {
      state.ingredients.push({
        id:       uid(),
        name:     ing.name,
        amount:   ing.amount || 1,
        unit:     ing.unit || 'oz',
        category: ing.category || autoCategory(ing.name),
      });
    });

    // Set drink name if provided and currently blank
    if (importedName) {
      const nameEl = root.querySelector('[data-bc="drinkName"]');
      if (nameEl && !nameEl.value) {
        nameEl.value = importedName;
        state.drinkName = importedName;
      }
    }

    closeImportModal();
    renderIngredients();
    renderSidebars();

    // Brief confirmation flash on the ingredients panel
    const panel = root.querySelector('.bc__panel--ingredients');
    if (panel) {
      panel.style.borderColor = 'var(--bc-brand-mid)';
      setTimeout(() => { panel.style.borderColor = ''; }, 1200);
    }
  }

  /* ============================================================
     SHOPIFY CART API
     ============================================================ */

  /**
   * Adds a syrup to the Shopify cart via /cart/add.js.
   * quantity = number of bottles calculated from the batch.
   * btn      = the button element, used for feedback states.
   */
  function addAllToCart(items, btn) {
    if (!items.length) return;
    btn.disabled = true;
    const orig = btn.textContent;
    btn.textContent = 'Adding…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items }),
    })
      .then(res => {
        if (!res.ok) throw new Error(res.status);
        return res.json();
      })
      .then(() => {
        const total = items.reduce((n, i) => n + i.quantity, 0);
        btn.textContent = 'Added ' + total + (total === 1 ? ' bottle ✓' : ' bottles ✓');
        btn.style.background = '#3A6020';
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('theme:cart:add', { bubbles: true }));
        if (window.Shopify && window.Shopify.onCartUpdate) {
          fetch('/cart.js').then(r => r.json()).then(cart => window.Shopify.onCartUpdate(cart));
        }
        setTimeout(() => {
          btn.textContent = orig;
          btn.disabled = false;
          btn.style.background = '';
        }, 4000);
      })
      .catch(err => {
        console.error('BG Reynolds cart error:', err);
        btn.textContent = 'Error — retry';
        btn.disabled = false;
      });
  }

  function addToCart(variant, quantity, btn) {
    // Accept either a full variant object {id} or legacy {variantId}
    const variantId = variant.id || variant.variantId;
    if (!variantId) return;

    const qty = Math.max(1, quantity || 1);

    // Optimistic UI — disable while request is in flight
    btn.disabled    = true;
    btn.textContent = 'Adding…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: qty }),
    })
      .then(res => {
        if (!res.ok) throw new Error(res.status);
        return res.json();
      })
      .then(() => {
        // Success state
        btn.textContent  = 'Added ✓';
        btn.style.background = '#3A6020';

        // Notify Shopify themes that listen for cart updates
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        // Dawn / Debut themes use this event
        document.dispatchEvent(new CustomEvent('theme:cart:add', { bubbles: true }));

        // Refresh the cart count bubble if the theme exposes a global
        if (window.Shopify && window.Shopify.onCartUpdate) {
          fetch('/cart.js').then(r => r.json()).then(cart => window.Shopify.onCartUpdate(cart));
        }

        // Re-enable after a moment with updated label
        setTimeout(() => {
          btn.textContent  = `Added (${qty})`;
          btn.disabled     = false;
          btn.style.background = '';
        }, 3000);
      })
      .catch(err => {
        console.error('BG Reynolds cart error:', err);
        btn.textContent = 'Error — retry';
        btn.disabled    = false;
        btn.style.background = '#4A1500';
        setTimeout(() => {
          btn.textContent = 'Add to cart';
          btn.style.background = '';
        }, 3000);
      });
  }

  /* ============================================================
     SHARE LINK
     ============================================================ */

  function buildShareState() {
    return {
      v: 3,
      n: state.drinkName,
      g: state.guests,
      dp: state.drinksPer,
      ss: state.servingSize,
      pm: state.portionMode,
      pb: state.preBatched,
      dil: state.dilution,
      adv: state.advancedMode,
      wst: state.wastage,
      ou: state.outputUnit,
      ings: state.ingredients.map(i => ({ nm: i.name, am: i.amount, u: i.unit, c: i.category })),
    };
  }

  function copyShareLink() {
    try {
      const encoded = btoa(encodeURIComponent(JSON.stringify(buildShareState())));
      const url = new URL(window.location.href);
      url.searchParams.set('bc', encoded);
      navigator.clipboard.writeText(url.href).then(() => showLinkStatus('Copied!')).catch(() => showLinkStatus('Copy failed'));
    } catch (e) { showLinkStatus('Error'); }
  }

  function showLinkStatus(msg) {
    root.querySelectorAll('[data-bc="linkStatus"]').forEach(el => {
      el.textContent = msg;
      setTimeout(() => { el.textContent = 'Copy share link'; }, 2500);
    });
  }

  function restoreFromUrl() {
    const encoded = new URLSearchParams(window.location.search).get('bc');
    if (!encoded) return false;
    try {
      const data = JSON.parse(decodeURIComponent(atob(encoded)));
      if (!data) return false;

      // Support v2 links (backwards compat)
      state.drinkName   = data.n  || '';
      state.guests      = data.g  || 1;
      state.drinksPer   = data.dp || 1;
      state.servingSize = data.ss || 'full';
      state.portionMode = data.pm || 'full';
      state.dilution    = data.dil != null ? data.dil : 0.2;
      state.preBatched  = data.pb  != null ? data.pb  : data.dil > 0;
      state.advancedMode = data.adv || false;
      state.wastage     = data.wst || 0;
      state.outputUnit  = data.ou  || 'oz';

      state.ingredients = (data.ings || []).map(i => ({
        id: uid(), name: i.nm || '', amount: i.am || 1,
        unit: i.u || 'oz', category: i.c || autoCategory(i.nm || ''),
      }));
      return true;
    } catch (e) { return false; }
  }

  function applyRestoredState() {
    const nameInput = bcq('drinkName');
    if (nameInput) nameInput.value = state.drinkName;

    const guestsInput = bcq('guests');
    if (guestsInput) guestsInput.value = state.guests;

    const drinksPerSel = bcq('drinksPer');
    if (drinksPerSel) drinksPerSel.value = state.drinksPer;

    // Dilution opts
    bcqq('dilutionOpt').forEach(opt => {
      const val = parseFloat(opt.getAttribute('data-value')) || 0;
      const isMatch = state.preBatched ? val > 0 : val === 0;
      opt.classList.toggle('bc__dilutionOpt--active', isMatch);
    });

    // Portion mode
    if (state.portionMode === 'fixed' && state.servingSize !== 'full') {
      // Restore the fixed pour value into the input fields
      const pourVal  = bcq('pourSizeValue');
      const pourUnit = bcq('pourSizeUnit');
      if (pourVal) {
        const ozVal = parseFloat(state.servingSize) || 6;
        pourVal.value = ozVal;
        if (pourUnit) pourUnit.value = 'oz';
      }
    }
    selectPortionMode(state.portionMode || 'full');

    // Wastage
    const wastageInput = bcq('wastage');
    if (wastageInput) wastageInput.value = state.wastage;

    // Dilution rate input
    const dilRateInput = bcq('dilutionRate');
    if (dilRateInput) dilRateInput.value = Math.round(state.dilution * 100);

    // Advanced mode
    if (state.advancedMode) {
      const panel = bcq('advancedPanel');
      const icon  = bcq('advancedToggleIcon');
      if (panel) panel.classList.remove('bc__advancedPanel--hidden');
      if (icon)  icon.classList.add('bc__advancedToggleIcon--open');
    }

    applyServingMethod();
  }

  /* ============================================================
     EVENT DELEGATION
     ============================================================ */

  function bindActions() {
    root.addEventListener('click', e => {
      const btn = e.target.closest('[data-bc-action]');
      if (!btn) return;
      switch (btn.getAttribute('data-bc-action')) {
        case 'go-step-1': goStep(1); break;
        case 'go-step-2': goStep(2); break;
        case 'go-step-3': goStep(3); break;
        case 'open-picker': openPicker(); break;
        case 'close-picker': closePicker(); break;
        case 'confirm-picker': confirmPicker(); break;
        case 'open-import': openImportModal(); break;
        case 'toggle-unit': toggleOutputUnit(); break;
        case 'close-import': closeImportModal(); break;
        case 'parse-recipe': parseRecipeWithAI(); break;
        case 'import-back':
          // 'Discard' on inline card — hide it and clear parsed state
          const previewCard = root.querySelector('[data-bc="importPreviewCard"]');
          if (previewCard) previewCard.hidden = true;
          importParsed = [];
          break;
        case 'confirm-import': confirmImport(); break;
        case 'copy-link': copyShareLink(); break;
        case 'print': window.print(); break;
        case 'toggle-advanced': toggleAdvancedMode(); break;
        case 'clear':
          state.ingredients = [];
          state.drinkName   = '';
          state.portionMode = 'full';
          state.servingSize = 'full';
          selectPortionMode('full');
          const nameInput = bcq('drinkName');
          if (nameInput) nameInput.value = '';
          qq('.bc__starterCard').forEach(c => c.classList.remove('bc__starterCard--selected'));
          renderIngredients(); renderSidebars(); goStep(1);
          break;
      }
    });

    // Step nav
    qq('[data-bc-step-btn]').forEach(btn =>
      btn.addEventListener('click', () => goStep(parseInt(btn.getAttribute('data-bc-step-btn'), 10)))
    );

    // Picker category tabs
    root.addEventListener('click', e => {
      const tab = e.target.closest('.bc__catTab');
      if (tab) switchPickerCat(tab.getAttribute('data-cat'));
    });

    // Picker search
    const pickerSearch = bcq('pickerSearch');
    if (pickerSearch) {
      pickerSearch.addEventListener('input', e => {
        state.pickerSelected = null;
        state.pickerCustom   = e.target.value;
        qq('.bc__pickerItem').forEach(i => i.classList.remove('bc__pickerItem--selected'));
      });
    }

    // Drink name
    const nameInput = bcq('drinkName');
    if (nameInput) nameInput.addEventListener('input', e => { state.drinkName = e.target.value; });

    // Guests
    const guestsInput = bcq('guests');
    if (guestsInput) guestsInput.addEventListener('input', e => {
      state.guests = Math.max(1, parseInt(e.target.value) || 1);
      renderSidebars();
    });

    // Drinks per
    const drinksPerSel = bcq('drinksPer');
    if (drinksPerSel) drinksPerSel.addEventListener('change', e => {
      state.drinksPer = parseInt(e.target.value) || 1;
      renderSidebars();
    });

    // Portion opts (event delegation)
    root.addEventListener('click', e => {
      const opt = e.target.closest('[data-bc="portionOpt"]');
      if (!opt) return;
      selectPortionMode(opt.getAttribute('data-portion') || 'full');
    });

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && state.pickerOpen) closePicker();
    });
  }

  /* ============================================================
     INIT
     ============================================================ */

  function init() {
    root = document.querySelector('[data-bc-root]');
    if (!root) return;

    const configEl = root.querySelector('[data-bc-config]');
    if (!configEl) return;

    try { config = JSON.parse(configEl.textContent); }
    catch (e) { console.error('BG Reynolds Batch Calculator: config parse error', e); return; }

    // Backwards compat: syrups as plain string array
    if (Array.isArray(config.syrups) && typeof config.syrups[0] === 'string') {
      config.syrups = config.syrups.map(t => ({ title: t, url: '', handle: '' }));
    }

    const restored = restoreFromUrl();

    if (!restored) {
      state.guests      = 1;
      state.drinksPer   = 1;
      state.servingSize = 'full';
      state.preBatched  = true;
      state.dilution    = parseFloat(config.dilution_rate) || 0.2;
      state.wastage     = 0;
      state.advancedMode = false;
      state.ingredients = [
        { id: uid(), name: '', amount: 1.5,  unit: 'oz', category: 'spirits' },
        { id: uid(), name: '', amount: 0.75, unit: 'oz', category: 'syrups'  },
        { id: uid(), name: '', amount: 0.75, unit: 'oz', category: 'juices'  },
      ];
    }

    // Set dilution rate input default from config
    const dilRateInput = bcq('dilutionRate');
    if (dilRateInput && !restored) dilRateInput.value = Math.round((parseFloat(config.dilution_rate) || 0.2) * 100);

    renderStarters();
    renderIngredients();
    initDilutionOpts();
    initPortionOpts();
    initAdvancedControls();
    bindActions();

    if (restored) applyRestoredState();

    renderSidebars();
    if (restored) goStep(3);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();